/**
 * React binding for the screenplay autosave scheduler on plain form pages.
 * The scheduler itself (debounce, backoff, permanent-vs-transient, flush
 * semantics) is screenplay/autosave.js unchanged — this hook only wires it
 * to React state and the page-lifecycle flush triggers.
 *
 * Contract with the page:
 *  - `data` is the live form state; the hook mirrors it into a ref so the
 *    scheduler reads the LATEST state at send time (never a queued copy).
 *  - `save` MUST be memoized (useCallback) on everything it closes over —
 *    the saver is recreated when `save` changes, and the outgoing saver's
 *    cleanup flush uses the OLD closure.
 *  - On retirement (save identity change / unmount) the outgoing saver's
 *    document is FROZEN at cleanup time. Without this, a level switch could
 *    interleave: old saver's follow-up flush reads the shared ref AFTER the
 *    new level's fetch landed, and PUTs the new level's data under the old
 *    level key — silent cross-level corruption.
 *  - call markDirty() on every user edit; flush() before programmatic
 *    navigation that must not lose work; isDirty() to block navigation
 *    when a flush could not land.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createAutosaver } from '../screenplay/autosave';
import { bodyByteSize, KEEPALIVE_BODY_LIMIT_BYTES } from '../screenplay/saveClient';

// The keepalive quota applies to the WIRE body ({...payload, base_rev}), but
// the hook only sees the form data. The wrapper (level/completed/base_rev
// keys + a 32-hex rev) is well under this margin.
const UNLOAD_BODY_MARGIN_BYTES = 512;

export function useFormAutosave({ data, save, enabled = true, canRideUnload }) {
  const dataRef = useRef(data);
  dataRef.current = data;
  // Multi-request savers (the plot canvas persists one PUT per dirty level,
  // sequentially — base_rev rotation forbids parallel) can only get their
  // FIRST request out during beforeunload; the rest die with the JS context.
  // canRideUnload(data) lets such a saver veto the "it rode along on
  // keepalive" assumption so the leave dialog still guards the writer.
  const canRideUnloadRef = useRef(canRideUnload);
  canRideUnloadRef.current = canRideUnload;
  const [status, setStatus] = useState(null);
  const saverRef = useRef(null);

  useEffect(() => {
    if (!enabled || !save) return undefined;
    // retired/frozenDoc pin the outgoing saver's document at retirement so
    // its final flush can never read a newer page's data through the shared
    // ref (see header comment).
    let retired = false;
    let frozenDoc = null;
    // Tracks whether flush() managed to dispatch a save synchronously during
    // beforeunload. When a save is already in flight, flush's first await is
    // that promise — nothing goes out before teardown, so the browser dialog
    // is the only backstop for the dirty content.
    let unloadSaveDispatched = false;
    const saver = createAutosaver({
      getDocument: () => (retired ? frozenDoc : dataRef.current),
      save: (doc, meta) => {
        if (meta?.unload) unloadSaveDispatched = true;
        return save(doc, meta);
      },
      onStatus: setStatus,
    });
    saverRef.current = saver;

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') saver.flush({ unload: true });
    };
    const onBeforeUnload = (event) => {
      unloadSaveDispatched = false;
      saver.flush({ unload: true });
      if (!saver.isDirty()) return;
      // Unsaved work survives page close only if a keepalive save went out
      // synchronously AND its body fits the byte quota (BYTES, not UTF-16
      // length). Otherwise ask the browser for the leave-confirmation dialog.
      const body = JSON.stringify(dataRef.current);
      const cannotRide =
        !unloadSaveDispatched ||
        (canRideUnloadRef.current ? !canRideUnloadRef.current(dataRef.current) : false) ||
        body == null ||
        bodyByteSize(body) >= KEEPALIVE_BODY_LIMIT_BYTES - UNLOAD_BODY_MARGIN_BYTES;
      if (cannotRide) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('beforeunload', onBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('beforeunload', onBeforeUnload);
      if (saverRef.current === saver) saverRef.current = null;
      // Freeze the document as of retirement — at this moment the shared ref
      // still holds the outgoing page/level's data (the new level's fetch
      // has not resolved yet), so the final flush writes the right content.
      retired = true;
      frozenDoc = dataRef.current;
      // flush() chains after any in-flight save and then saves the final
      // state — one sequenced PUT, never a second concurrent racer. Destroy
      // only AFTER it settles (mirrors ScreenplayEditor); destroying first
      // would skip the follow-up save and strand anything typed during an
      // in-flight request. No keepalive here — the page lives on across SPA
      // navigation; keepalive is for page-hide/close.
      saver
        .flush()
        .catch((err) => console.error('autosave: final save on unmount failed', err))
        .finally(() => saver.destroy());
    };
  }, [enabled, save]);

  const markDirty = useCallback(() => saverRef.current?.markDirty(), []);
  const flush = useCallback((meta) => saverRef.current?.flush(meta) ?? Promise.resolve(), []);
  const isDirty = useCallback(() => saverRef.current?.isDirty() ?? false, []);

  return { status, markDirty, flush, isDirty };
}
